import React, { FC, useCallback } from 'react';

import * as actions from '../mfaTokensActions';
import { Field } from '../types';
import { DUO_MFA_TOKEN_FIELDS } from '../../../common/fields';
import TokenTextField from './TokenTextField';
import TokenActions from './TokenActions';
import { TokenComponentProps } from './';
import UserIdentifierDropdown from './UserIdentifierDropdown';

type DispatchFn = (action: UpdateAction) => void;

interface UpdateAction {
    type: typeof actions.UPDATE_DUO_MFA_TOKEN;
    payload: {
        fieldId: string;
        value: string;
    };
}

export interface DuoFields {
    duoUpnFieldId: Field;
    duoApiHostnameId: Field;
    duoIntegrationKeyId: Field;
    duoSecretKeyId: Field;
}

export type DuoProps = TokenComponentProps<DuoFields, DispatchFn>;

const Duo: FC<DuoProps> = ({ fields, dispatch, onSave, onCancel }) => {
    const duoUpn = fields[DUO_MFA_TOKEN_FIELDS.DUO_UPN_FIELD.ID];
    const duoApiHostname = fields[DUO_MFA_TOKEN_FIELDS.DUO_API_HOSTNAME.ID];
    const duoIntegrationKey = fields[DUO_MFA_TOKEN_FIELDS.DUO_INTEGRATION_KEY.ID];
    const duoSecretKey = fields[DUO_MFA_TOKEN_FIELDS.DUO_SECRET_KEY.ID];

    const isSomeFieldsEmpty = [duoApiHostname, duoIntegrationKey, duoSecretKey].some((field) => !field.value);

    const textFields = [
        { field: duoApiHostname, isSecret: false, placeholder: 'api-{my-id}.duosecurity.com' },
        { field: duoIntegrationKey, isSecret: true, placeholder: 'Enter key' },
        { field: duoSecretKey, isSecret: true, placeholder: 'Enter key' }
    ];

    const dispatchUpdate = useCallback(
        (fieldId: string, value: string) => {
            dispatch({
                type: actions.UPDATE_DUO_MFA_TOKEN,
                payload: { fieldId, value }
            });
        },
        [dispatch]
    );

    const duoUpnOptions = (duoUpn.options || []).map((option) => ({
        title: option.label,
        value: option.value
    }));

    return (
        <>
            <UserIdentifierDropdown
                options={duoUpnOptions}
                selectedOption={duoUpn.value}
                onChange={(val) => dispatchUpdate(duoUpn.id, val)}
                label={duoUpn.name}
                mandatory
            />

            {textFields.map(({ field, isSecret, placeholder }) => (
                <TokenTextField
                    key={field.id}
                    label={field.name}
                    value={field.value}
                    isSecret={isSecret}
                    placeholder={placeholder}
                    onChange={(val) => dispatchUpdate(field.id, val)}
                />
            ))}

            <TokenActions onSave={onSave} onCancel={onCancel} saveDisabled={isSomeFieldsEmpty} />
        </>
    );
};

export default Duo;
